import { boardLink } from '../utils/board-navigation';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Detail, Field, FieldList, Notice, Tag } from '../../../common/components/detail/detail';
import { Gate } from '../../../global/contexts/auth/auth';
import { useProjectName, useRepositoryInfo, useTierLabel } from '../../../global/contexts/lookups/lookups';
import KindBadge from '../../repositories/layouts/kind-badge';
import AuthorizeTask from '../../breakdown/layouts/authorize-task';
import Authorship from '../../audit/layouts/authorship';
import { useTaskAuthorship } from '../../audit/contexts/authorship';
import { useTask } from '../contexts/tasks';
import { WORKFLOW_STATE_LABEL } from '../utils/form';
import { isOneOff } from '../utils/modality';
import ReviewHistory from '../../reviews/layouts/history';
import DirectWork from './direct';
import UnblockTask from './unblock';
import TaskTrail from './trail';

import '../../../common/components/detail/styles/detail.css';
import '../styles/trail.css';

// Leer una tarea, que antes exigía `tasks:write` porque la única pantalla
// detrás de /admin/tasks/:id era el formulario de edición.
//
// SE LEE CONTRA EL PAQUETE, no contra la fila. `GET /admin/tasks/{id}` dejó de
// ser un CRUD plano con T16.2: devuelve `TaskDetailPackage`, y la diferencia no
// es cosmética —
//
//   · el estado se llama `state`, no `workflow_state`;
//   · `acceptance_criteria` y `scope` viajan ARRIBA, no dentro de `task`;
//   · el tier y el repositorio vienen en `where`, con su nombre;
//   · y hay bloques que la fila no tenía: `commits`, `reviews`,
//     `review_rounds`, `reservation`.
//
// Se lee con respaldo a la fila plana igualmente: el contrato publica el
// esquema de la respuesta solo en algunas rutas, y una instancia atrasada tiene
// que degradar en vez de romperse.

const STATE_TONE = {
    ready:     'active',
    done:      'active',
    claimed:   'accent',
    in_review: 'accent',
    blocked:   'danger',
    returned:  'warn',
    discarded: 'danger',
    proposed:  'neutral',
};

const KIND_LABEL = { feature: 'Feature', fix: 'Fix', test: 'Test' };

// Las dos lecturas de una tarea, en pestañas. La trayectoria ya contaba QUÉ
// pasó en cada ronda; lo que faltaba es con qué se midió: los criterios tal
// como estaban al entregar, el dictamen de cada uno y la bitácora.
//
// Pestaña y no una sección más abajo porque son dos preguntas distintas —qué
// es esta tarea, y cómo le fue en revisión— y la segunda es larga: tres
// entregas devueltas son tres rondas con sus criterios y sus bitácoras.
//
// `?tab=revisiones` en la URL, como en Proyectos: una ronda se cita pasando un
// enlace, no diciendo «abrí la tarea y hacé clic».
const TABS = [
    ['detalle', 'Detalle'],
    ['revisiones', 'Revisiones'],
];

export default function TaskDetail({ taskId }) {
    const { data, isLoading, error } = useTask(taskId);
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const tierLabel = useTierLabel();
    const projectName = useProjectName();
    const repositoryInfo = useRepositoryInfo();

    if (isLoading) return <p className="client-form__loading">Cargando tarea...</p>;

    if (error) {
        // A 404 here is also what "out of your scope" looks like, on purpose:
        // a 403 would already confirm the task exists. The message must not
        // undo that by guessing which of the two it was.
        return (
            <p className="client-form__failure">
                {error.status === 404
                    ? 'Esta tarea no está disponible: no existe, o queda fuera de tu alcance.'
                    : error.message || 'No se pudo cargar la tarea'}
            </p>
        );
    }

    const detail = data ?? {};
    const task = detail.task ?? detail;

    // The package spells it `state`; the plain row spelled it `workflow_state`.
    const workflowState = task.state ?? task.workflow_state;
    const state = WORKFLOW_STATE_LABEL[workflowState] ?? workflowState;

    const criteria = detail.acceptance_criteria ?? task.acceptance_criteria ?? [];
    const scope = detail.scope ?? task.scope ?? [];

    // NOMBRES, NO IDS.
    //
    // The package answers the tier and the repository WITH their names, in
    // `where` — so those two stop being `#57`. The project does not travel at
    // all, only `project_id`, so its name is resolved from the catalog; and the
    // repository's `kind` is dropped by the package even though the assembler
    // loaded the whole row, so the badge comes from there too.
    const tier = detail.where?.tier ?? null;
    const repository = detail.where?.repository ?? null;
    const repositoryRow = repositoryInfo(task.repository_id);
    const oneOff = isOneOff(task);
    const tierChip = tier ? { short: tier.name, full: tier.name } : tierLabel(task.tier_id);
    // Where a one-off task came from, when it came from somewhere.
    const origin = task.origin
        ? `${task.origin.type === 'incident' ? 'Incidente' : 'Ticket'} #${task.origin.id}`
        : null;

    const reservation = detail.reservation ?? null;

    // Cuántas rondas hay sale del paquete, que ya está leído: la pestaña puede
    // decir el número sin pedir nada, y las rondas enteras se piden solo si
    // alguien la abre.
    const rounds = detail.review_rounds ?? [];
    const legacyReviews = detail.reviews ?? [];
    const tab = params.get('tab') === 'revisiones' ? 'revisiones' : 'detalle';

    const setTab = (value) => {
        const target = new URLSearchParams(params);
        if (value === 'detalle') target.delete('tab');
        else target.set('tab', value);
        setParams(target, { replace: true });
    };

    return (
        <section className="detail-page">
            <header className="detail-head">
                <div className="detail-head__text">
                    <h2 className="detail-head__title">{task.title}</h2>
                    <div className="detail-head__tags">
                        <Tag>Tarea #{task.id}</Tag>
                        {/* A one-off task has no tier: said, instead of an empty
                            chip where the tier would be. */}
                        {oneOff && <Tag tone="accent" title="Sin tier ni plan">Puntual</Tag>}
                        <Tag tone={STATE_TONE[workflowState] ?? 'neutral'}>{state}</Tag>
                        {task.kind && <Tag>{KIND_LABEL[task.kind] ?? task.kind}</Tag>}
                        {task.estimate && <Tag title="Talla">{task.estimate}</Tag>}
                        {tierChip && <Tag title={tierChip.full ?? undefined}>{tierChip.short}</Tag>}
                        {detail.attempts > 0 && (
                            <Tag tone={detail.attempts > 1 ? 'warn' : 'neutral'}>
                                {detail.attempts === 1 ? '1 revisión' : `${detail.attempts} revisiones`}
                            </Tag>
                        )}
                    </div>
                </div>

                <div className="detail-head__actions">
                    <Link to={boardLink('/admin/development/tareas', params)} className="detail-head__action detail-head__action--quiet">
                        Volver
                    </Link>
                    {/* TSS vite/05 — <Gate> inside JSX, never an inline can().
                        Hiding the link does not replace the server's check; it
                        stops offering a door the API would shut. */}
                    <Gate domain="tasks:write">
                        {workflowState === 'blocked' && <UnblockTask key={task.id} task={task} />}
                        <Link
                            to={boardLink(`/admin/tasks/${task.id}/edit`, params)}
                            className={`detail-head__action${workflowState === 'proposed' ? ' detail-head__action--quiet' : ''}`}
                        >
                            Editar
                        </Link>
                    </Gate>
                    {/* The next step of a proposal, so it is the primary
                        action and "Editar" steps back. A planned task is
                        authorized within its tier; a one-off task, by its own
                        route — the modal knows which. */}
                    {workflowState === 'proposed' && (task.tier_id || oneOff) && (
                        <Gate domain="tasks:authorize">
                            <AuthorizeTask
                                task={task}
                                tierId={task.tier_id}
                                // Back to the board, on this task's project, where
                                // it now sits among the ready ones.
                                onAuthorized={() => navigate(
                                    boardLink('/admin/development/tareas', params),
                                )}
                            />
                        </Gate>
                    )}
                </div>
            </header>

            {workflowState === 'proposed' && (
                <Notice>
                    Es una <strong>propuesta</strong>. La cola no la entrega: hace falta que una
                    persona la autorice para que pase a estar lista para tomar
                    {task.tier_id
                        ? ', con «Autorizar» arriba o en lote desde el desglose de su tier'
                        : oneOff ? ', con «Autorizar» arriba' : ''}.
                </Notice>
            )}

            {/* La razón de la última devolución viaja aparte en el paquete, y va
                arriba por eso mismo: es lo primero que alguien quiere leer. */}
            {detail.last_return?.reason && (
                <Notice tone="warn">
                    <strong>Última devolución:</strong> {detail.last_return.reason}
                </Notice>
            )}

            <nav className="detail-tabs" aria-label="Vistas de la tarea">
                {TABS.map(([slug, text]) => (
                    <button
                        key={slug}
                        type="button"
                        className={`detail-tabs__tab${tab === slug ? ' detail-tabs__tab--on' : ''}`}
                        onClick={() => setTab(slug)}
                        aria-pressed={tab === slug}
                    >
                        {text}
                        {slug === 'revisiones' && (rounds.length > 0 || legacyReviews.length > 0) && (
                            <span className="detail-tabs__count">
                                {rounds.length || legacyReviews.length}
                            </span>
                        )}
                    </button>
                ))}
            </nav>

            {tab === 'revisiones' ? (
                <ReviewHistory
                    taskId={task.id}
                    fallbackCriteria={criteria}
                    legacyReviews={legacyReviews}
                />
            ) : (
            <>
            <TaskAuthorship taskId={task.id} tierId={task.tier_id} />

            <Detail>
                {/* EL REPOSITORIO, CON SU MODO GRÁFICO: la misma insignia que la
                    lista de repositorios — forma, tono y palabra. */}
                <Field
                    label="Repositorio"
                    value={repository || repositoryRow ? (
                        <span className="detail-repo">
                            {/* La misma insignia que el listado de repositorios:
                                un componente, para que la misma cosa se vea
                                igual en las dos pantallas. */}
                            {repositoryRow && <KindBadge kind={repositoryRow.kind} />}
                            <Link to={`/admin/repositories/${task.repository_id}`}>
                                {repository?.name ?? repositoryRow?.name}
                            </Link>
                            {repository?.main_branch && (
                                <span className="detail-repo__branch">{repository.main_branch}</span>
                            )}
                        </span>
                    ) : null}
                    empty="Sin repositorio"
                />

                <Field
                    label="Proyecto"
                    value={task.project_id ? (
                        <Link to={`/admin/projects/${task.project_id}`}>
                            {projectName(task.project_id)}
                        </Link>
                    ) : null}
                />

                <Field
                    label="Tier"
                    value={tier ? (
                        <Link to={`/admin/tiers/${tier.id}`}>
                            {tier.name}
                            {tier.phase && <span className="detail-repo__branch">{tier.phase}</span>}
                        </Link>
                    ) : null}
                    empty={oneOff ? 'Ninguno: es una tarea puntual, no sale de un plan ni de un tier' : 'Sin tier'}
                />

                <Field
                    label="Peso de prioridad"
                    value={task.priority != null
                        ? `${task.priority} — mayor peso primero dentro de su grupo`
                        : null}
                />

                {oneOff && (
                    <Field label="Motivo" value={task.reason} empty="Sin motivo registrado" wide markdown />
                )}
                {oneOff && origin && <Field label="Origen" value={origin} />}
                <Field label="Descripción" value={task.description} empty="Sin descripción" wide markdown />
                <FieldList label="Criterios de aceptación" items={criteria} empty="Ninguno — no se puede autorizar así" />
                <FieldList label="Ámbito" items={scope} empty="Sin rutas declaradas" />
            </Detail>

            {/* Quién, con qué, cuándo, y qué se decidió. */}
            <TaskTrail detail={{ ...detail, task: { ...task, state: workflowState } }} />

            {/* Directing the work is `tasks:write`, the same permission the
                three routes ask for. Hiding the panel does not replace the
                server's check; it stops offering a door the API would shut. */}
            <Gate domain="tasks:write">
                <DirectWork task={{ ...task, workflow_state: workflowState, assigned_to: task.assigned_to, assigned_user: task.assigned_user, priority: task.priority, id: task.id }} />
            </Gate>

            {/* Counted, never named. A dependency outside your scope still
                blocks the work; showing its id would be the leak the backend
                took the trouble to avoid. */}
            {task.hidden_dependencies > 0 && (
                <Notice>
                    Además depende de <strong>{task.hidden_dependencies}</strong>
                    {' '}tarea{task.hidden_dependencies === 1 ? '' : 's'} fuera de tu alcance.
                </Notice>
            )}

            {/* `reservation` sólo dice quién la tiene o la hizo; el responsable
                asignado es otra cosa y vive en el panel de arriba. */}
            {!reservation && workflowState === 'ready' && (
                <Notice>
                    Nadie la está trabajando: está en la cola, esperando que un agente la tome.
                </Notice>
            )}
            </>
            )}
        </section>
    );
}

// Who wrote it, who changed its specification last, who signed it. The task
// table records none of it; the event log does.
function TaskAuthorship({ taskId, tierId }) {
    const { authorship, readable, isLoading } = useTaskAuthorship(taskId, tierId);

    return (
        <Authorship
            readable={readable}
            isLoading={isLoading}
            rows={[
                {
                    label: 'Escrita por',
                    actor: authorship.written,
                    hint: authorship.writtenHow === 'proposed' ? 'en un lote del desglose' : null,
                },
                { label: 'Última edición', actor: authorship.edited },
                { label: 'Autorizada por', actor: authorship.authorized },
            ]}
        />
    );
}
